import React, { useEffect, useMemo, useRef, useState } from "react"
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap'
import ReactPaginate from 'react-paginate'
import { useHistory } from "react-router"
import { countCompareThuocsiPrice, searchCompareThuocsiPrice, thuocsiCrawlDates } from "../../../api"
import { formatPrice } from "../../../utils"
import { useAsync } from "../../../utils/hooks"
import Loading from "../../shared/Loading"
import { useRecoilValue } from "recoil"
import { accountDataState } from "../../../recoil/account"
import { Parser } from "json2csv"

enum outputHeadersEnum {
	no = "STT",
	medigoCode = "Mã SP Medigo",
	productName = "Tên sản phẩm",
    tsProductCode = "Mã SP Thuốc sỉ",
    tsSkuCode = "Mã SKU Thuốc sỉ",
    medigoPrice = "Giá Medigo",
    tsPrice = "Giá Thuốc sỉ",
    absoluteDiffPrice = "Lệch giá (VNĐ)",
    percentDiffPrice = "Lệch giá (%)",
    image = "Hình ảnh",
    stockStatus = "Tình trạng kho hàng",
}

enum DiffPriceFilter {
    greater = "greater",
    less = "less",
    all = "all"
}

enum StockStatusFilter {
    all = "ALL",
    outOfStock = "OUT_OF_STOCK",
    inStock = "IN_STOCK"
}

enum DiffPriceSort {
    absoluteDesc = "mAbsoluteDiffPrice DESC",
    absoluteAsc = "mAbsoluteDiffPrice ASC",
    percentAsc = "mPercentDiffPrice ASC",
    percentDesc = "mPercentDiffPrice DESC"
}

enum ManufacturerFilter {
    MEDX = "MEDX",
    ALL = "ALL"
}

interface IState {
    offset: number,
    limit: number,
    filter: DiffPriceFilter,
    stockStatus: StockStatusFilter,
    selectedManufacturer: ManufacturerFilter,
    order: DiffPriceSort,
    listProduct: Array<any>,
    crawlDates: Array<string>,
    selectedCrawlDate: string,
    total?: number,
}

const initState: IState = {
    offset: 0,
    limit: 100,
    filter: DiffPriceFilter.greater,
    stockStatus: StockStatusFilter.all,
    selectedManufacturer: ManufacturerFilter.MEDX,
    order: DiffPriceSort.absoluteDesc,
    listProduct: [],
    crawlDates: [],
    selectedCrawlDate: "",
    total: 0,
}

const CompareThuocsiPrice = React.memo(() => {
    const searchCompareThuocsiPriceAsync = useAsync(searchCompareThuocsiPrice)
    const countCompareThuocsiPriceAsync = useAsync(countCompareThuocsiPrice)
    
    const accountInfo = useRecoilValue(accountDataState)
    const adminId = useMemo(() => {
        if (accountInfo.loginSession.userLocalType === 'ADMIN') return accountInfo.doctor.mId
    }, [accountInfo])

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [outRows, setOutRows] = useState<any[]>([]);

    const [state, setState] = useState<IState>(initState)
    const tableProduct: any = useRef()
    const page: any = useRef()

    const backToTop = () => {
        if (!tableProduct.current?.scrollTop) return
        tableProduct.current.scrollTop = 0
    }

    useEffect(() => {
        const fetchData = async () => {
            const result = await thuocsiCrawlDates();
            setState({ ...state, crawlDates: result, selectedCrawlDate: result[0] })
        }
        fetchData();
    }, [adminId])

    useEffect(() => {
        backToTop()

        if (state.selectedCrawlDate) {

            searchCompareThuocsiPriceAsync.execute(
                {
                    limit: state.limit,
                    offset: state.offset,
                    order: getPriceOrder(state.filter, state.order),
                    where: {
                        mCrawlDate: state.selectedCrawlDate,
                        mAbsoluteDiffPrice: getPriceFilter(state.filter),
                        mStockStatus: getStockStatusFilter(state.stockStatus),
                    },
                },
                state.selectedManufacturer == ManufacturerFilter.MEDX
            ).then(res => {
                setState(prev => ({ ...prev, listProduct: res }))
            })
            countCompareThuocsiPriceAsync.execute(
                {
                    mCrawlDate: state.selectedCrawlDate,
                    mAbsoluteDiffPrice: getPriceFilter(state.filter),
                    mStockStatus: getStockStatusFilter(state.stockStatus),
                },
                state.selectedManufacturer == ManufacturerFilter.MEDX
            ).then(res => {
                setState(prev => ({ ...prev, total: res }))
            })
        }
    }, [state?.offset, state?.limit, state?.filter, state?.order, state?.selectedCrawlDate, state?.selectedManufacturer, state?.stockStatus])

	const onDownloadClick = async () => {
		if (isLoading) {
			return;
		}

		setIsLoading(true);

		let outRows: any[] = [];

		let items = await searchCompareThuocsiPrice(
			{
                where: {
                    mCrawlDate: state.selectedCrawlDate ? state.selectedCrawlDate : undefined,
                },
                order: "mAbsoluteDiffPrice DESC",
            },
            false
		);

		if (items) {
			let totalItems = items.length;

			for (let i = 0; i < totalItems; ++i) {
				let row: any = {};

				const item = items[i];

				row[outputHeadersEnum.no] = i + 1;
				row[outputHeadersEnum.medigoCode] = item.mMedigoCode;
				row[outputHeadersEnum.productName] = item.mProductName;
				row[outputHeadersEnum.tsProductCode] = item.mTsProductCode;
                row[outputHeadersEnum.tsSkuCode] = item.mTsSkuCode;
                row[outputHeadersEnum.medigoPrice] = item.mMedigoPrice;
                row[outputHeadersEnum.tsPrice] = item.mTsPrice;
                row[outputHeadersEnum.absoluteDiffPrice] = item.mAbsoluteDiffPrice;
                row[outputHeadersEnum.percentDiffPrice] = item.mPercentDiffPrice;
                row[outputHeadersEnum.image] = item.mImage;
                row[outputHeadersEnum.stockStatus] = item.mStockStatus != 'OUT_OF_STOCK' ? 'Còn hàng' : 'Hết hàng';

				outRows.push(Object.assign({}, row));
			}
		}
		setOutRows(outRows);
        setIsLoading(false);

        let parser = new Parser();
		let csv = parser.parse(outRows);

		var blob = new Blob([csv], { type: "text/csv;charset=utf8;" });

		var uri = "data:attachment/csv;charset=utf-8," + encodeURI(csv);
		var link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.setAttribute("visibility", "hidden");
		// link.download = selectedFile.name;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

	};

    return <>
        <div id="main" style={{
            width: "100%",
            maxWidth: "1000px !important",
            height: '100vh',
            marginTop: '1rem',
            minHeight: '-webkit-fill-available',
        }}
        >

            {
                <>
                    <h3 className="d-flex py-3 justify-content-center text-center" style={{ fontSize: 25 }}>
                        So sánh giá B2B với Thuốc sỉ
                    </h3>

                    <div className="d-flex">
                        <div className="w-50 mr-3 d-flex" >
                            <Form.Label className='m-0 mr-1 d-flex justify-content-center align-items-center'>
                                <b style={{ whiteSpace: 'nowrap' }}>Lọc </b>
                            </Form.Label>
                            <Form.Control onChange={(e) => {
                                const value = e.target.value as DiffPriceFilter;
                                setState(prev => ({ ...prev, filter: value }))
                            }} name='filter' as="select" value={state.filter} custom>
                                <option value={DiffPriceFilter.greater}>Cao hơn</option>
                                <option value={DiffPriceFilter.less}>Thấp hơn</option>
                            </Form.Control>
                        </div>
                        <div className="w-50 mr-3 d-flex" >
                            <Form.Label className='m-0 mr-1 d-flex justify-content-center align-items-center'>
                                <b style={{ whiteSpace: 'nowrap' }}>Sắp xếp </b>
                            </Form.Label>
                            <Form.Control onChange={(e) => {
                                const value = e.target.value as DiffPriceSort;
                                setState(prev => ({ ...prev, order: value }))
                            }} name='filter' as="select" value={state.order} custom>
                                <option value={DiffPriceSort.absoluteDesc}>Lệch(VNĐ)</option>
                                <option value={DiffPriceSort.percentDesc}>Lệch(%)</option>
                            </Form.Control>
                        </div>
                        <div className="w-50 d-flex align-items-center">
                            <Form.Label className='m-0 mr-1 d-flex justify-content-center align-items-center'>
                                <b style={{ whiteSpace: 'nowrap' }}>Ngày cập nhật</b>
                            </Form.Label>
                            <Form.Control onChange={(e) => {
                                const value = e.target.value
                                setState(prev => ({ ...prev, selectedCrawlDate: value }))
                            }} name='filter' as="select" value={state.selectedCrawlDate} custom>
                                {state.crawlDates.map(crawlDate => <option key={crawlDate} value={crawlDate}>{crawlDate.replace(/(\d{4})(\d{2})(\d{2})/g, '$3-$2-$1')}</option>)}
                            </Form.Control>
                        </div>
                    </div>
                    <div className="d-flex">
                        <div className="w-50 d-flex mt-3 mr-3">
                            <Form.Label className='m-0 mr-1 d-flex justify-content-center align-items-center'>
                                <b style={{ whiteSpace: 'nowrap' }}>Nhà cung cấp</b>
                            </Form.Label>
                            <Form.Control onChange={(e) => {
                                const value = e.target.value as ManufacturerFilter;
                                setState(prev => ({ ...prev, selectedManufacturer: value }))
                            }} name='manufacturer' as="select" value={state.selectedManufacturer} custom>
                                <option value={ManufacturerFilter.MEDX}>Thuốc sỉ</option>
                                <option value={ManufacturerFilter.ALL}>Tất cả</option>
                            </Form.Control>
                        </div>
                        <div className="w-50 d-flex mt-3 mr-3">
                            <Form.Label className='m-0 mr-1 d-flex justify-content-center align-items-center'>
                                <b style={{ whiteSpace: 'nowrap' }}>Tình trạng</b>
                            </Form.Label>
                            <Form.Control onChange={(e) => {
                                const value = e.target.value as StockStatusFilter;
                                setState(prev => ({ ...prev, stockStatus: value }))
                            }} name='stockStatus' as="select" value={state.stockStatus} custom>
                                <option value={StockStatusFilter.all}>Tất cả</option>
                                <option value={StockStatusFilter.inStock}>Còn hàng</option>
                                <option value={StockStatusFilter.outOfStock}>Hết hàng</option>
                            </Form.Control>
                        </div>
                        <a href="/#" onClick={
                            (e) => {
                                e.preventDefault();
                                onDownloadClick();
                            }
                            } className={"upload-tab-title mt-3 active"}>
                            Tải file so sánh giá
                        </a>
                    </div>
                    {
                        state.listProduct.length === 0 &&
                        <div className="d-flex py-3 justify-content-center align-items-center">
                            <p className="m-0">Không tìm thấy sản phẩm nào</p>
                        </div>
                    }

                    <div className="row mt-2"></div>

                    {
                        state.listProduct.length !== 0 &&
                        <>
                            <div ref={tableProduct} className="table-products" style={{ position: "relative", maxHeight: 'unset' }}>
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Tên</th>
                                            <th style={{ width: 50 }}>SKU ID</th>
                                            <th style={{ width: 50 }}>Giá Medigo</th>
                                            <th style={{ width: 50 }}>Giá TS</th>
                                            <th style={{ width: 50 }}>Lệch (VND)</th>
                                            <th style={{ width: 50 }}>Lệch (%)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            state.listProduct?.map((product: any) => {
                                                return <tr style={{ opacity: isMEDXProduct(product.mTsSkuCode) ? 1 : 0.4 }}>
                                                    <td>
                                                        <OverlayTrigger placement="top" overlay={(<Tooltip id={product.mId} className="show"><img style={{maxWidth: '150px'}} src={product.mImage}/></Tooltip>)}>
                                                            <p>{product.mProductName}</p>
                                                        </OverlayTrigger>
                                                    </td>
                                                    <td style={{ width: 60 }}>
                                                        {formatPrice(product.mMedigoCode)}
                                                    </td>
                                                    <td style={{ width: 60, color: product.mMedigoPrice > product.mTsPrice ? 'red' : undefined }}>
                                                        {formatPrice(product.mMedigoPrice)}
                                                    </td>
                                                    <td style={{ width: 60 }}>
                                                        {formatPrice(product.mTsPrice)}
                                                    </td>
                                                    <td style={{ width: 60 }}>
                                                        {formatPrice(Math.abs(product.mAbsoluteDiffPrice))}
                                                    </td>
                                                    <td style={{ width: 60 }}>
                                                        {product.mPercentDiffPrice.toFixed(2)}
                                                    </td>
                                                </tr>
                                            })
                                        }

                                    </tbody>
                                </table>
                                {
                                    searchCompareThuocsiPriceAsync.status == "loading" &&
                                    <div className="wrap-loading">
                                        <Loading />
                                    </div>
                                }
                            </div>
                            <div className="d-flex align-items-center justify-content-start my-3 px-3">
                                <div className="pagination mt-3">
                                    {
                                        state.total &&
                                        <ReactPaginate
                                            pageClassName='page-item'
                                            breakLabel="..."
                                            onPageChange={(page: any) => {
                                                setState(prev => ({ ...prev, offset: state.limit * page.selected }))
                                            }}
                                            pageCount={Math.ceil(state.total / state.limit)}
                                            pageRangeDisplayed={1}
                                            marginPagesDisplayed={1}
                                            previousLabel={<b className="pagination-navigator">{'<'}</b>}
                                            nextLabel={<b className="pagination-navigator"> {'>'} </b>}
                                            forcePage={(state.offset) / state.limit}
                                        />
                                    }
                                </div>
                                <Form.Control
                                    className="select-page-limit"
                                    onChange={(e) => {
                                        const value = e.target.value
                                        setState(prev => ({
                                            ...prev,
                                            limit: +value,
                                        }))
                                    }
                                    }
                                    name='limit'
                                    as="select"
                                    value={state.limit}
                                    custom
                                >
                                    <option value="5">5</option>
                                    <option value='10'>10</option>
                                    <option value='20'>20</option>
                                    <option value='50'>50</option>
                                    <option value='100'>100</option>
                                </Form.Control>
                            </div>
                            <div className="d-flex align-items-center justify-content-end mb-4">
                                <div className="d-flex align-items-center justify-content-end mr-3"
                                    style={{ width: '60%' }}
                                >
                                    <b>Tổng số {state.total} sản phẩm</b>
                                </div>
                                <div className="d-flex align-items-center mr-3 justify-content-end"
                                    style={{ width: '40%' }}>
                                    <Form.Control type="number" className="mr-2" placeholder="Đến trang"
                                        ref={page}
                                        min={1}
                                        style={{ width: '68%', height: '30px' }} />
                                    <Form.Control
                                        as="button"
                                        onClick={() => {
                                            if (page.current.value > 0 && state.total && page.current.value <= Math.ceil(state.total / state.limit)) {
                                                setState(prev => ({ ...prev, offset: state.limit * (page.current.value - 1) }))
                                            }
                                        }}
                                        style={{ width: '40px', height: '30px', color: '#fff', backgroundColor: '#36cd7a', border: 'none' }}
                                    >Đi</Form.Control>
                                </div>
                            </div>
                        </>
                    }
                </>
            }
        </div>
    </>
})

export default CompareThuocsiPrice;

function getPriceFilter(filter: DiffPriceFilter) {
    switch (filter) {
        case DiffPriceFilter.greater:
            return {
                gte: 0,
            }
        case DiffPriceFilter.less:
            return {
                lt: 0,
            }
        case DiffPriceFilter.all:
            return {

            }
        default:
            break;
    }
}

function getPriceOrder(filter: DiffPriceFilter, order: DiffPriceSort) {
    switch (filter) {
        case DiffPriceFilter.greater:
            if (order == DiffPriceSort.absoluteDesc) {
                return DiffPriceSort.absoluteDesc;
            } else {
                return DiffPriceSort.percentDesc;
            }
        case DiffPriceFilter.less:
            if (order == DiffPriceSort.percentDesc) {
                return DiffPriceSort.percentDesc;
            } else {
                return DiffPriceSort.absoluteAsc;
            }
    
        default:
            break;
    }
}

const getStockStatusFilter = (stockStatus: StockStatusFilter) => {
    if (stockStatus === StockStatusFilter.all) {
        return undefined
    } else if (stockStatus === StockStatusFilter.outOfStock) {
        return StockStatusFilter.outOfStock;
    } else {
        return {
            neq: StockStatusFilter.outOfStock
        }
    }
    
}

function isMEDXProduct(skuCode: string) {
    return skuCode.startsWith('MEDX');
}
